import {
  NEW_ROUND_AMOUNT_INVESTED_LABEL,
  NEW_ROUND_INPUTS_HEADER,
  NEW_ROUND_INVESTMENT_DATE_LABEL,
  NEW_ROUND_INVESTMENT_DATE_TOOLTIP,
  NEW_ROUND_OPTION_POOL_REFRESH_LABEL,
  NEW_ROUND_OPTION_POOL_REFRESH_TOOLTIP,
  NEW_ROUND_POOL_REFRESH_MODE_LABEL,
  NEW_ROUND_POOL_REFRESH_MODE_TOOLTIP,
  NEW_ROUND_PRE_MONEY_VALUATION_LABEL,
  NEW_ROUND_PRE_MONEY_VALUATION_TOOLTIP,
  NEW_ROUND_SECURITY_NAME_LABEL,
  NEW_ROUND_SECURITY_NAME_TOOLTIP,
  POOL_REFRESH_MODE_POST,
  POOL_REFRESH_MODE_POST_LABEL,
  POOL_REFRESH_MODE_PRE,
  POOL_REFRESH_MODE_PRE_LABEL,
  TWO_DECIMAL_PLACES,
} from 'common/constants/cap-table';
import { costBasisCurrencyFormat } from 'common/formats/formats';
import { GridDate, GridDateEditor, GridDateValueViewer, GridSelect } from 'components/FeaturedSpreadsheet/components';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import SelectValueViewer from 'components/SelectValueViewer';
import { gridShortDate } from 'utilities';
import { NEW_ROUND_MODELER_TEST_IDS } from '../NewRoundModeler.testIds';
import { labelCell, sectionHeaderRow, VALUE_STYLE } from './sheetCellHelpers';
import { NrmParserProps, NrmTableData } from './types';

// gridType:'percentage' stores a fraction (0.0978); display rounds to TWO_DECIMAL_PLACES ("9.78%").
const optionPoolRefreshFormat = {
  style: 'percent',
  isDecimal: true,
  minimumFractionDigits: TWO_DECIMAL_PLACES,
  maximumFractionDigits: TWO_DECIMAL_PLACES,
  validateFloat: true,
};

const poolRefreshModeOptions = [
  { value: POOL_REFRESH_MODE_PRE, label: POOL_REFRESH_MODE_PRE_LABEL },
  { value: POOL_REFRESH_MODE_POST, label: POOL_REFRESH_MODE_POST_LABEL },
];

const inputsParser = ({ tableData }: NrmParserProps) => ({
  ...sectionHeaderRow(NEW_ROUND_INPUTS_HEADER),
  A2: labelCell(NEW_ROUND_PRE_MONEY_VALUATION_LABEL, NEW_ROUND_PRE_MONEY_VALUATION_TOOLTIP),
  A3: labelCell(NEW_ROUND_AMOUNT_INVESTED_LABEL),
  A4: labelCell(NEW_ROUND_OPTION_POOL_REFRESH_LABEL, NEW_ROUND_OPTION_POOL_REFRESH_TOOLTIP),
  A5: labelCell(NEW_ROUND_POOL_REFRESH_MODE_LABEL, NEW_ROUND_POOL_REFRESH_MODE_TOOLTIP),
  A6: labelCell(NEW_ROUND_SECURITY_NAME_LABEL, NEW_ROUND_SECURITY_NAME_TOOLTIP),
  A7: labelCell(NEW_ROUND_INVESTMENT_DATE_LABEL, NEW_ROUND_INVESTMENT_DATE_TOOLTIP),
  B2: {
    key: 'B2',
    alias: 'preMoney',
    value: Number(tableData.preMoney) || 0,
    gridType: 'number' as const,
    format: costBasisCurrencyFormat,
    readOnly: false,
    isRequired: true,
    greaterThan: 0,
    style: VALUE_STYLE,
    testId: NEW_ROUND_MODELER_TEST_IDS.preMoney,
  },
  B3: {
    key: 'B3',
    alias: 'amountInvested',
    value: Number(tableData.amountInvested) || 0,
    gridType: 'number' as const,
    format: costBasisCurrencyFormat,
    readOnly: false,
    isRequired: true,
    greaterThan: 0,
    style: VALUE_STYLE,
    testId: NEW_ROUND_MODELER_TEST_IDS.amountInvested,
  },
  B4: {
    key: 'B4',
    alias: 'optionPoolRefresh',
    // tableData.optionPoolRefresh is the percent-number string ("9.7800") the rest of
    // NRM's state/calc functions use - convert to the fraction this cell actually stores.
    value: (Number(tableData.optionPoolRefresh) || 0) / 100,
    gridType: 'percentage' as const,
    format: optionPoolRefreshFormat,
    readOnly: false,
    style: VALUE_STYLE,
    testId: NEW_ROUND_MODELER_TEST_IDS.optionPoolRefresh,
  },
  B5: {
    key: 'B5',
    alias: 'poolRefreshMode',
    value: tableData.poolRefreshMode,
    gridType: 'string' as const,
    dropdown: true,
    readOnly: false,
    dataEditor: (props: any) => <GridSelect menuPosition="absolute" options={poolRefreshModeOptions} {...props} />,
    valueViewer: (props: any) => <SelectValueViewer options={poolRefreshModeOptions} {...props} />,
    style: VALUE_STYLE,
  },
  B6: {
    key: 'B6',
    alias: 'securityName',
    value: tableData.securityName,
    gridType: 'string' as const,
    readOnly: false,
    isRequired: true,
    style: VALUE_STYLE,
  },
  B7: {
    key: 'B7',
    alias: 'investmentDate',
    value: gridShortDate(tableData.investmentDate),
    gridType: 'gridDate' as const,
    readOnly: false,
    isRequired: true,
    dataEditor: (props: any) => <GridDateEditor {...props} />,
    valueViewer: (props: any) => <GridDateValueViewer {...props} />,
    gridDateComponent: GridDate,
    format: null,
    style: VALUE_STYLE,
  },
});

const createInputsSheetConfig = (tableData: NrmTableData) =>
  new SpreadsheetConfig<object, object, object, object>({
    name: 'nrmInputs',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    parser: inputsParser as any,
    reverseParser: undefined,
    afterCellChanged: (changes: any) => changes,
    rowConfig: [{}, {}, {}, {}, {}, {}, {}],
    columns: [{}, {}],
    tableData,
    showTitlesColumn: false,
    hasColTitle: false,
    showToolbar: false,
    displayRowIndicator: false,
    displayColumnIndicator: false,
    currencyFormatter: false,
    unitsFormatter: false,
    ignoreCellExpressionErrors: false,
    // SpreadsheetConfigConstructorParams extends ScalarSpreadsheetConfigDataInterface which
    // requires internally-computed fields (evaluate, applyConditions, reverseParse, etc.)
    // that the constructor populates itself — not caller inputs.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any);

export default createInputsSheetConfig;
